import { Box, Divider, ListItemText, ListSubheader, MenuList, Typography } from "@mui/material";
import type { Ref } from "react";
import { useEffect, useRef } from "react";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import type { RelativeDateValue } from "../../../../../../utils/relativeDates.ts";
import {
  getRelativeDateLabel,
  getRelativeDatePreview,
  relativeDayValues,
  relativePeriodValues,
} from "../../../../../../utils/relativeDates.ts";

interface RowProps {
  value: RelativeDateValue;
  selected: boolean;
  rowRef?: Ref<HTMLLIElement>;
  onSelect: (value: RelativeDateValue) => void;
}

const ReferenceDateRow = ({ value, selected, rowRef, onSelect }: RowProps) => (
  <MenuItemStyled ref={rowRef} selected={selected} onClick={() => onSelect(value)}>
    <ListItemText>{getRelativeDateLabel(value)}</ListItemText>
    <Typography variant="caption" color="textSecondary" sx={{ pl: 2 }}>
      {getRelativeDatePreview(value)}
    </Typography>
  </MenuItemStyled>
);

interface Props {
  selectedValue: RelativeDateValue | undefined;
  hideDays?: boolean;
  hidePeriods?: boolean;
  onSelect: (value: RelativeDateValue) => void;
}

const ReferenceDateList = ({ selectedValue, hideDays, hidePeriods, onSelect }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const selected = selectedRef.current;
    if (!container || !selected) {
      return;
    }
    container.scrollTo({ top: selected.offsetTop - container.clientHeight / 2 + selected.clientHeight / 2 });
  }, []);

  return (
    <Box ref={containerRef} sx={{ maxHeight: "22rem", overflowY: "auto", width: "100%", position: "relative" }}>
      <MenuList disablePadding>
        {!hideDays && <ListSubheader disableSticky>Day</ListSubheader>}
        {!hideDays &&
          relativeDayValues.map((value) => (
            <ReferenceDateRow
              key={value}
              value={value}
              selected={value === selectedValue}
              rowRef={value === selectedValue ? selectedRef : undefined}
              onSelect={onSelect}
            />
          ))}
        {!hideDays && !hidePeriods && <Divider />}
        {!hidePeriods && <ListSubheader disableSticky>Period</ListSubheader>}
        {!hidePeriods &&
          relativePeriodValues.map((value) => (
            <ReferenceDateRow
              key={value}
              value={value}
              selected={value === selectedValue}
              rowRef={value === selectedValue ? selectedRef : undefined}
              onSelect={onSelect}
            />
          ))}
      </MenuList>
    </Box>
  );
};

export default ReferenceDateList;
